/* eslint-disable */
import axios, { AxiosResponse } from 'axios';
import { ArtistTrackModel } from '@/models';

const datasetsBase = process.env.VUE_APP_HF_DATASETS_BASE_URL || '/api/suno';
const dataset = process.env.VUE_APP_SUNO_DATASET || 'TTxdfcgv7/suno';
const config = process.env.VUE_APP_SUNO_DATASET_CONFIG || 'default';
const split = process.env.VUE_APP_SUNO_DATASET_SPLIT || 'train';
const estimatedRows = Number(process.env.VUE_APP_SUNO_DATASET_ROWS || 659788);
const rowsTimeout = Number(process.env.VUE_APP_SUNO_ROWS_TIMEOUT || 10000);
const searchTimeout = Number(process.env.VUE_APP_SUNO_SEARCH_TIMEOUT || 8000);
const genreAttempts = Number(process.env.VUE_APP_SUNO_GENRE_ATTEMPTS || 4);
const popularityAttempts = Number(process.env.VUE_APP_SUNO_POPULARITY_ATTEMPTS || 3);
const popularityWindow = Number(process.env.VUE_APP_SUNO_POPULARITY_WINDOW || 5000);
const genreFilterWindow = Number(process.env.VUE_APP_SUNO_GENRE_FILTER_WINDOW || 3000);
const genreFilterAliasesLimit = Number(process.env.VUE_APP_SUNO_GENRE_FILTER_ALIASES || 4);
const recentLimit = Number(process.env.VUE_APP_SUNO_RECENT_LIMIT || 500);
const sunoSearchEnabled = process.env.VUE_APP_ENABLE_SUNO_SEARCH !== 'false';

let sunoCallCount = 0;
const recentTrackIds: string[] = [];
const recentTrackIdSet = new Set<string>();

interface SunoDatasetRow {
  id: string;
  user_id?: string;
  audio_url?: string;
  video_url?: string;
  image_url?: string;
  image_large_url?: string;
  display_name?: string;
  handle?: string;
  avatar_image_url?: string;
  is_public?: boolean;
  is_trashed?: boolean;
  status?: string;
  title?: string;
  play_count?: number;
  upvote_count?: number;
  created_at?: string;
  metadata_tags?: string;
  metadata_prompt?: string;
  metadata_gpt_description_prompt?: string;
  metadata_duration?: number;
}

interface DatasetRow<T> {
  row_idx: number;
  row: T;
}

interface DatasetRowsResponse<T> {
  rows: Array<DatasetRow<T>>;
}

interface SunoArtistModel {
  id: string;
  image: string;
  joindate: string;
  name: string;
  shareurl: string;
  shorturl: string;
  website: string;
}

const genreAliases: { [key: string]: string[] } = {
  rock: ['rock', 'alt rock', 'alternative rock', 'indie rock', 'hard rock', 'punk', 'metal', 'grunge'],
  jazz: ['jazz', 'swing', 'bebop', 'smooth jazz', 'jazz fusion', 'bossa nova'],
  electronic: ['electronic', 'electronica', 'edm', 'electro', 'techno', 'house', 'trance', 'synthwave', 'dubstep', 'drum and bass', 'dnb'],
  classical: ['classical', 'orchestral', 'orchestra', 'symphony', 'baroque', 'piano', 'violin', 'opera'],
  hiphop: ['hiphop', 'hip hop', 'rap', 'trap', 'boom bap', 'drill'],
  pop: ['pop', 'dance pop', 'synth pop', 'kpop', 'jpop', 'electropop'],
  acoustic: ['acoustic', 'folk', 'singer songwriter', 'unplugged', 'guitar', 'country'],
  ambient: ['ambient', 'atmospheric', 'cinematic', 'meditation', 'chill', 'soundscape'],
  dark: ['dark', 'ambient', 'atmospheric', 'cinematic', 'gothic', 'melancholic'],
}

const state = {
  tracks: []
}

const mutations = {
  setTracks(state: any, payload: ArtistTrackModel[]) {
    state.tracks = payload
  },
}

const getters = {
  getTracks(state: any) {
    return state.tracks
  }
}

function normalizeGenre(genre?: string) {
  if (!genre) {
    return ''
  }
  return genre.toLowerCase().replace(/[-_]/g, '').replace(/\s+/g, '')
}

function normalizeSearchValue(value?: string) {
  if (!value) {
    return ''
  }

  return value.toLowerCase().replace(/^@/, '').replace(/[^a-z0-9]+/g, '')
}

function getSearchQueries(query: string) {
  const raw = query.trim()
  const variants = [
    raw,
    raw.toLowerCase(),
    normalizeSearchValue(raw),
    raw.replace(/^@/, ''),
  ].filter(Boolean)

  return Array.from(new Set(variants))
}

function getGenreAliases(genre?: string) {
  const normalizedGenre = normalizeGenre(genre)
  if (!normalizedGenre) {
    return []
  }
  return genreAliases[normalizedGenre] || [genre || normalizedGenre]
}

function hasGenre(row: SunoDatasetRow, genre?: string) {
  const aliases = getGenreAliases(genre)
  if (!aliases.length) {
    return true
  }

  const haystack = [
    row.metadata_tags,
    row.title,
    row.metadata_prompt,
    row.metadata_gpt_description_prompt,
  ].filter(Boolean).join(' ').toLowerCase().replace(/[-_]/g, '').replace(/\s+/g, '')

  return aliases.some((alias) => haystack.includes(normalizeGenre(alias)))
}

function isPlayable(row: SunoDatasetRow) {
  return Boolean(
    row.audio_url &&
    row.status === 'complete' &&
    row.is_public !== false &&
    row.is_trashed !== true
  )
}

function getImage(row: SunoDatasetRow) {
  return row.avatar_image_url || ''
}

function getArtistName(row: SunoDatasetRow) {
  return row.display_name || row.handle || 'Suno Creator'
}

function mapSunoTrack(row: SunoDatasetRow): ArtistTrackModel {
  const image = getImage(row)
  const title = row.title && row.title.trim() ? row.title.trim() : 'Untitled Suno Track'
  const artistName = getArtistName(row)
  const createdDate = row.created_at ? row.created_at.substring(0, 10) : ''

  return {
    id: `suno-${row.id}`,
    name: title,
    artist_id: `suno-${row.user_id || row.handle || artistName}`,
    artist_name: artistName,
    album_id: 'suno',
    album_image: image,
    album_name: 'Suno',
    audio: row.audio_url || '',
    audiodownload: '',
    audiodownload_allowed: false,
    duration: row.metadata_duration ? Math.round(row.metadata_duration).toString() : '0',
    image,
    joindate: row.created_at || '',
    website: `https://suno.com/song/${row.id}`,
    license_ccurl: `https://huggingface.co/datasets/${dataset}`,
    releasedate: createdDate,
    tracks: []
  } as ArtistTrackModel
}

function getPopularityScore(row: SunoDatasetRow) {
  const playCount = Math.max(0, row.play_count || 0)
  const upvoteCount = Math.max(0, row.upvote_count || 0)

  return Math.log10(playCount + 1) + Math.log10(upvoteCount + 1) * 3
}

function rankRowsForRadio(rows: SunoDatasetRow[]) {
  return rows
    .map((row) => ({
      row,
      score: getPopularityScore(row) + Math.random() * 0.85
    }))
    .sort((a, b) => b.score - a.score)
    .map((item) => item.row)
}

function uniqueTracks(tracks: ArtistTrackModel[], limit: number) {
  const seen = new Set<string>()
  const unique: ArtistTrackModel[] = []

  for (const track of tracks) {
    if (seen.has(track.id)) {
      continue
    }
    seen.add(track.id)
    unique.push(track)
    if (unique.length >= limit) {
      break
    }
  }

  return unique
}

function uniqueRows(rows: SunoDatasetRow[]) {
  const seen = new Set<string>()
  const unique: SunoDatasetRow[] = []

  rows.forEach((row) => {
    if (!row.id || seen.has(row.id)) {
      return
    }
    seen.add(row.id)
    unique.push(row)
  })

  return unique
}

function isRecentRow(row: SunoDatasetRow) {
  return recentTrackIdSet.has(`suno-${row.id}`)
}

function selectTracks(rows: SunoDatasetRow[], limit: number, genre?: string, avoidRecent = false) {
  const candidates = rankRowsForRadio(uniqueRows(rows)
    .filter((row) => isPlayable(row) && hasGenre(row, genre))
  )

  if (!avoidRecent) {
    return uniqueTracks(candidates.map(mapSunoTrack), limit)
  }

  const freshTracks = candidates
    .filter((row) => !isRecentRow(row))
    .map(mapSunoTrack)

  let tracks = uniqueTracks(freshTracks, limit)

  if (tracks.length < limit) {
    const fallbackTracks = candidates
      .filter((row) => isRecentRow(row))
      .map(mapSunoTrack)
    tracks = uniqueTracks(tracks.concat(fallbackTracks), limit)
  }

  return tracks
}

function rememberTracks(tracks: ArtistTrackModel[]) {
  tracks.forEach((track) => {
    if (!track.id || recentTrackIdSet.has(track.id)) {
      return
    }
    recentTrackIds.push(track.id)
    recentTrackIdSet.add(track.id)
  })

  while (recentTrackIds.length > recentLimit) {
    const oldTrackId = recentTrackIds.shift()
    if (oldTrackId) {
      recentTrackIdSet.delete(oldTrackId)
    }
  }
}

function createOffset(length: number) {
  const pages = Math.max(1, Math.floor((estimatedRows - length) / length))
  const randomPage = Math.floor(Math.random() * pages)
  const rotatingPage = (randomPage + sunoCallCount) % pages
  sunoCallCount++
  return rotatingPage * length
}

function createPopularityOffset(length: number) {
  const pages = Math.max(1, Math.floor(popularityWindow / length))
  return Math.floor(Math.random() * pages) * length
}

function createGenreFilterOffset(length: number, salt = 0) {
  const pages = Math.max(1, Math.floor(genreFilterWindow / length))
  const randomPage = Math.floor(Math.random() * pages)
  const rotatingPage = (randomPage + sunoCallCount + salt) % pages
  sunoCallCount++
  return rotatingPage * length
}

function extractRows(response: AxiosResponse<DatasetRowsResponse<SunoDatasetRow>>) {
  return (response.data.rows || []).map((item) => item.row)
}

function fetchRows(length: number, offset?: number) {
  return axios.get<DatasetRowsResponse<SunoDatasetRow>>(`${datasetsBase}/rows`, {
    params: {
      dataset,
      config,
      split,
      offset: offset !== undefined ? offset : createOffset(length),
      length,
    },
    timeout: rowsTimeout,
  }).then(extractRows)
}

function fetchFilteredRows(where: string, length: number, offset: number, orderby?: string) {
  return axios.get<DatasetRowsResponse<SunoDatasetRow>>(`${datasetsBase}/filter`, {
    params: {
      dataset,
      config,
      split,
      where,
      ...(orderby ? { orderby } : {}),
      offset,
      length,
    },
    timeout: rowsTimeout,
  }).then(extractRows)
}

function fetchPopularRows(length: number, offset?: number) {
  return fetchFilteredRows(
    `"status"='complete' AND "is_public"=true AND "is_trashed"=false`,
    length,
    offset !== undefined ? offset : createPopularityOffset(length),
    `"play_count" DESC`
  )
}

function escapeFilterValue(value: string) {
  return value.replace(/'/g, "''").replace(/[%_]/g, ' ').trim()
}

function createGenreFilterWhere(alias: string) {
  return `"status"='complete' AND "is_public"=true AND "is_trashed"=false AND "metadata_tags" LIKE '%${escapeFilterValue(alias)}%'`
}

async function fetchGenreRows(genre: string, length: number, offset?: number) {
  const aliases = getGenreAliases(genre)
    .map(escapeFilterValue)
    .filter(Boolean)
    .slice(0, genreFilterAliasesLimit)

  if (!aliases.length) {
    return []
  }

  const batches = await Promise.all(
    aliases.map((alias, index) => (
      fetchFilteredRows(
        createGenreFilterWhere(alias),
        length,
        offset !== undefined ? offset : createGenreFilterOffset(length, index)
      ).catch(() => [])
    ))
  )

  let rows = uniqueRows(([] as SunoDatasetRow[]).concat(...batches))

  if (rows.length < Math.min(length, 30)) {
    const fallbackBatches = await Promise.all(
      aliases.map((alias) => fetchFilteredRows(createGenreFilterWhere(alias), length, 0).catch(() => []))
    )
    rows = uniqueRows(rows.concat(...fallbackBatches))
  }

  return rows
}

function searchRows(query: string, length: number, offset?: number) {
  return axios.get<DatasetRowsResponse<SunoDatasetRow>>(`${datasetsBase}/search`, {
    params: {
      dataset,
      config,
      split,
      query,
      offset: offset !== undefined ? offset : (sunoCallCount++ * length) % 500,
      length,
    },
    timeout: searchTimeout,
  }).then(extractRows)
}

async function searchRowsForQuery(query: string, length: number) {
  const batches = await Promise.all(
    getSearchQueries(query).map((searchQuery) => searchRows(searchQuery, length, 0).catch(() => []))
  )

  return uniqueRows(([] as SunoDatasetRow[]).concat(...batches))
}

function rowMatchesArtistQuery(row: SunoDatasetRow, query: string) {
  const normalizedQuery = normalizeSearchValue(query)
  if (!normalizedQuery) {
    return false
  }

  const displayName = normalizeSearchValue(row.display_name)
  const handle = normalizeSearchValue(row.handle)

  return displayName.includes(normalizedQuery) || handle.includes(normalizedQuery)
}

function mapRowsToArtists(rows: SunoDatasetRow[], query: string, limit = 30): SunoArtistModel[] {
  const seen = new Set<string>()
  const artists: SunoArtistModel[] = []

  uniqueRows(rows)
    .filter((row) => isPlayable(row) && rowMatchesArtistQuery(row, query))
    .sort((a, b) => getPopularityScore(b) - getPopularityScore(a))
    .forEach((row) => {
      const id = `suno-${row.user_id || row.handle || getArtistName(row)}`
      if (seen.has(id) || artists.length >= limit) {
        return
      }
      seen.add(id)
      artists.push({
        id,
        name: getArtistName(row),
        image: row.avatar_image_url || getImage(row),
        joindate: row.created_at || '',
        shareurl: row.handle ? `https://suno.com/@${row.handle}` : '',
        shorturl: row.handle ? `https://suno.com/@${row.handle}` : '',
        website: row.handle ? `https://suno.com/@${row.handle}` : '',
      })
    })

  return artists
}

const actions = {
  async fetchRadioTracks({commit}: {commit: Function}, payload: { limit: number; genre?: string; offset?: number; remember?: boolean; excludeRecent?: boolean }) {
    const limit = Math.min(payload.limit || 40, 100)
    const batchLength = Math.min(100, Math.max(limit * 2, limit))
    let rows: SunoDatasetRow[] = []

    try {
      const attempts = payload.genre ? genreAttempts : popularityAttempts
      const batches = await Promise.all([
        ...(payload.genre ? [fetchGenreRows(payload.genre, batchLength, payload.offset).catch(() => [])] : []),
        fetchPopularRows(batchLength, payload.offset).catch(() => []),
        ...Array.from({ length: attempts }).map((_, index) => (
          fetchRows(batchLength, index === 0 ? payload.offset : undefined).catch(() => [])
        ))
      ])
      rows = ([] as SunoDatasetRow[]).concat(...batches)
    } catch (error) {
      rows = await fetchRows(batchLength, payload.offset)
    }

    const tracks = selectTracks(rows, limit, payload.genre, payload.excludeRecent === true)

    if (payload.remember) {
      rememberTracks(tracks)
    }

    commit('setTracks', tracks)
    return tracks
  },

  async searchCatalog({commit}: {commit: Function}, query: string) {
    if (!sunoSearchEnabled || !query || query.trim().length < 3) {
      commit('setTracks', [])
      return { tracks: [], users: [] }
    }

    try {
      const rows = await searchRowsForQuery(query, 100)
      const tracks = selectTracks(rows, 30)
      const users = mapRowsToArtists(rows, query)
      commit('setTracks', tracks)
      return { tracks, users }
    } catch (error) {
      commit('setTracks', [])
      return { tracks: [], users: [] }
    }
  },

  async searchTracks({dispatch}: {dispatch: Function}, query: string) {
    const result = await dispatch('searchCatalog', query)
    return result.tracks
  },

  async searchUsers({dispatch}: {dispatch: Function}, query: string) {
    if (!sunoSearchEnabled || !query || query.trim().length < 3) {
      return []
    }

    const result = await dispatch('searchCatalog', query)
    return result.users
  }
}

const sunoModule = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}

export default sunoModule;
